import axios from "axios"
import { useCallback, useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { ImEye, ImEyeBlocked } from "react-icons/im"
import { useHistory } from "react-router-dom"
import { Alert, Breadcrumb, BreadcrumbItem, Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap"
import './Login.scss'

import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js/max"
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { ClipLoader } from "react-spinners"
import Cookies from "universal-cookie"
import { AppContext } from "../../components/App"
import { ROUTE } from "../../components/Routes"
import { error } from "../../components/Utils/Error"
import { getPaysFromLangue, getReseauFromLangue, getSocieteFromLangue } from "../../components/Utils/ReseauFromLangue"
import { PaysType } from "../../components/Utils/Types"
import { useDomaine } from "../../components/Utils/Url"
import { immatriculationName, valideImmatriculation } from "../../components/Utils/ValideImmatriculation"
import { GlobalStoreAction } from "../../config/Lite-Redux/reducerGlobal"

type PropsType = { panier?: boolean }

export default function Login({ panier = false }: PropsType) {
  const { t, i18n } = useTranslation()

  const [state, dispatch] = useContext(AppContext)
  const domaine = useDomaine()
  const history = useHistory()
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [passwordType, setPasswordType] = useState<any>("password")
  const [showAlert, setShowAlert] = useState(false)
  const [listePays, setListePays] = useState<PaysType[] | undefined>([])

  const [loadingConnexion, setLoadingConnexion] = useState(false)
  const [loadingFormulairePanier, setLoadingFormulairePanier] = useState(false)
  const [loadingInscription, setLoadingInscription] = useState(false)
  const langue: string = localStorage.getItem("i18nextLng") ?? 'fr-FR'
  const { executeRecaptcha } = useGoogleReCaptcha()

  const emailValidator: RegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  const [isEmailInvalid, setIsEmailInvalid] = useState<boolean>(!emailValidator.test(login))

  const [stateFormPanier, setStateFormPanier] = useState({
    immat: "",
    pays: getPaysFromLangue(langue),
    nom: "",
    mobile: "",
    societe: getSocieteFromLangue(langue),
    langue: langue,
    reseau: getReseauFromLangue(langue)
  })



  // Traitement de l'action (token) connexionRedirectionSwitch
  useEffect(() => {
    const cookies = new Cookies()

    const tokenJWT = cookies.get('tokenJWT')
    const idTiersEntreprise = cookies.get('idTiersEntreprise')
    const route: string = cookies.get('route')

    if (tokenJWT && idTiersEntreprise && route) {
      axios.get('/auth/me', { headers: { 'content-type': 'application/json', Authorization: `Bearer ${tokenJWT}` } })
        .then(response => {
          const informationWeb = response.data

          // Connexion automatique si il existe un cookie informationWeb contenant les information de connexion
          dispatch({ type: 'CONNECT_USER', value: informationWeb })
          if (informationWeb.interlocuteur.langue !== undefined && i18n.language.toUpperCase() !== informationWeb.interlocuteur.langue.toUpperCase()) {
            i18n.changeLanguage(informationWeb.interlocuteur.langue)
          }

          // Selection de l'entreprise idTiersEntreprise
          dispatch({ type: GlobalStoreAction.CHOIX_ENTREPRISE, value: Number(idTiersEntreprise) })
          // axios.post('/auth/me/updateLastConnexion')

          // Redrection vers la page souhaité
          if (route in ROUTE) {
            history.push((ROUTE as any)[route])
          } else {
            history.push(ROUTE.DASHBOARD)
          }

          cookies.remove('tokenJWT')
          cookies.remove('idTiersEntreprise')
        })
    }
  }, [dispatch, history, i18n])


  useEffect(() => {
    if (panier) {
      const URL_API: string = `/liste/pays`
      axios.get(URL_API, { headers: { 'X-langue': langue } })
        .then(response => {
          setListePays(response.data)
        }
        ).catch(erreur => error(erreur))
    }
  }, [langue, panier])

  /*** handlers ***/
  const handleInputLogin = (event: any) => {
    setIsEmailInvalid(!emailValidator.test(event.target.value))
    setLogin(event.target.value)
  }
  const handleInputPassword = (event: any) => { setPassword(event.target.value) }
  const handleCloseAlert = () => { setShowAlert(false) }

  const onClickConnexion = useCallback(async (event: any) => {
    event.preventDefault()
    setLoadingConnexion(true)

    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }
    const newToken = await executeRecaptcha('connexion');
    const objRequest = '{ "login": "' + login?.trim() + '", "password": "' + password + '", "recaptcha": "' + newToken + '"}'
    axios.post('/auth', objRequest,
      { headers: { 'content-type': 'application/json' } }
    ).then((response) => {
      if (response.status === 200 && response.data !== "") {
        dispatch({ type: 'CONNECT_USER', value: response.data })
        console.log(response, i18n, i18n.language)
        if (response.data.interlocuteur.langue !== undefined && i18n.language.toUpperCase() !== response.data.interlocuteur.langue.toUpperCase()) {
          i18n.changeLanguage(response.data.interlocuteur.langue)
        }
        if (panier && state.redirection) {
          history.push(state.redirection || ROUTE.LOGIN)
        } else {
          history.push(ROUTE.CHOIX_ENTREPRISE)
        }
      } else {
        setMessage(t('LOGIN.erreur_login_mdp'))
        setShowAlert(true)
      }
    }).catch((erreur) => {
      console.error(erreur)

      setMessage(t('LOGIN.erreur_login_mdp'))
      setShowAlert(true)
    })
      .finally(() => setLoadingConnexion(false))
  }, [executeRecaptcha, dispatch, history, i18n, login, password, t, state, panier])

  const onClickInscription = (event: any) => {
    event.preventDefault()
    setLoadingInscription(true)
    history.push(ROUTE.INSCRIPTION)
    setLoadingInscription(false)
  }

  const onClickPanier = (event: any) => {
    event.preventDefault()
    setLoadingFormulairePanier(true)
    event.currentTarget.disabled = true;
    if (!isInValidPhoneNumber(stateFormPanier.mobile) && valideImmatriculation(stateFormPanier.immat, stateFormPanier.pays)) {
      dispatch({ type: GlobalStoreAction.USER_PANIER, value: stateFormPanier })
      history.push(state.redirection ?? ROUTE.LOGIN)
    }
    setLoadingFormulairePanier(false)
  }

  const handleChangePanier = (event: any) => {
    let value = event.target.value ? event.target.value : '';
    if (event.target.name !== 'nom') {
      value = value.trim();
    }
    setStateFormPanier({
      ...stateFormPanier,
      [event.target.name]: value,
    });
  }

  const isInValidPhoneNumber = (mobile: string) => {
    console.log('isInValidPhoneNumber')
    if (stateFormPanier && mobile && stateFormPanier.pays)
      if (isValidPhoneNumber(mobile, stateFormPanier.pays as any)) {
        console.log('isInValidPhoneNumber', mobile)
        const phoneNumber = parsePhoneNumber(mobile, stateFormPanier.pays as any)
        console.log('isInValidPhoneNumber', mobile, phoneNumber, phoneNumber.getType())
        console.log(phoneNumber.getType())
        return phoneNumber.getType() !== 'MOBILE'
      }
    return true
  }

  const getComptInscription = () => {
    return <Form className='form-inscription' onSubmit={onClickInscription}>
      <Row className="mb-4">
        <div><p>{t('LOGIN.jai_pas_encore')} {t('LOGIN.de_compte_macc')}</p></div>
      </Row >
      <Row>
        <Button className="action-button" type="submit" disabled={loadingInscription}>
          {t('LOGIN.LABEL.inscrivez_vous')}
          <ClipLoader className="loadSpinner" color="#ffffff" size={15} loading={loadingInscription} />
        </Button>
      </Row>
    </Form>
  }

  const onClickEye = (event: any) => {
    event.preventDefault()
    setPasswordType(passwordType === 'password' ? 'text' : 'password')
  }

  const getFormulairePanier = () => {
    return <Form className='form-panier' onSubmit={onClickPanier}>
      <Row>
        <Label><p>{t('LOGIN.jai_pas_encore')} <b>{t('LOGIN.de_compte_macc')}</b></p></Label>

        <Col sm={12} md={8} lg={8}>
          <FormGroup floating>
            <Input id='immatriculation'
              type="text"
              name='immat'
              onChange={handleChangePanier}
              value={stateFormPanier.immat}
              invalid={!valideImmatriculation(stateFormPanier.immat, stateFormPanier.pays)}
              required={stateFormPanier.pays === 'FR' || stateFormPanier.immat !== ''}
              placeholder={immatriculationName(stateFormPanier.pays)}
            />
            <Label for='immatriculation'>{immatriculationName(stateFormPanier.pays)} {stateFormPanier.pays === 'FR' ? <sup>*</sup> : ''}</Label>
          </FormGroup>
        </Col>
        <Col sm={12} md={4} lg={4}>
          <FormGroup floating>
            <Input type="select" name="pays" id="pays" onChange={handleChangePanier} required={true}>
              {listePays?.map((pays: PaysType) =>
                <option key={pays.code} value={pays.code} selected={pays.code === stateFormPanier.pays} >{pays.label}</option>
              )}
            </Input>

          </FormGroup>
        </Col>
        <Col sm={12} md={12} lg={12}>
          <FormGroup floating>
            <Input id='nom' type="text" name='nom' onChange={handleChangePanier} value={stateFormPanier.nom} required={true}
              placeholder={t('FORMULAIRE.LABEL.nom')}
            />
            <Label for='nom'>{t('FORMULAIRE.LABEL.nom')}<sup>*</sup></Label>
          </FormGroup>
        </Col>
        <Col sm={12} md={12} lg={12}>
          <FormGroup floating>
            <Input id='mobile' type="text" name='mobile' onChange={handleChangePanier}
              value={stateFormPanier.mobile}
              required={true}
              invalid={isInValidPhoneNumber(stateFormPanier.mobile)}
              placeholder={t('FORMULAIRE.LABEL.mobile')}
            />
            <Label for='mobile'>{t('FORMULAIRE.LABEL.mobile')}<sup>*</sup></Label>
          </FormGroup>
        </Col>
      </Row>
      <Row className='justify-content-center'>
        <Col sm={12} md={10} lg={8}  >
          <Button className="action-button" type="submit" disabled={loadingFormulairePanier}>
            {t('LOGIN.LABEL.valider')}
            <ClipLoader className="loadSpinner" color="#ffffff" size={15} loading={loadingFormulairePanier} />
          </Button>
        </Col>
      </Row>

    </Form>
  }

  return (
    <div className='Login'>
      <Breadcrumb>
        <BreadcrumbItem>
          <a href={domaine}>{t('NAVLINK.NAME.accueil')}</a>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <a href={process.env.PUBLIC_URL + ROUTE.DASHBOARD}>{t('NAVLINK.NAME.mon-compte')}</a>
        </BreadcrumbItem>
        <BreadcrumbItem active>
          {t('NAVLINK.NAME.connexion')}
        </BreadcrumbItem>
      </Breadcrumb>
      {panier
        ? <h3>{t('LOGIN.connectez-vous-pour-continuer')}</h3>
        : <h1>{t('LOGIN.titre')}</h1>}
      <Row>
        <Col sm={12} lg={6} className='col-login'>
          <Form inline className={panier ? 'form-login-panier' : 'form-login'} onSubmit={onClickConnexion}>
            <FormGroup floating id='loginMail' type="email"                                                                                                                                                                                                                       >
              <Input
                id='InputLogin'
                className={isEmailInvalid ? "invalid" : "valid"}
                onChange={handleInputLogin}
                type='email' placeholder=' '
                invalid={isEmailInvalid}
                valid={!isEmailInvalid}
                required />
              <Label htmlFor='InputLogin'>{t('LOGIN.LABEL.e-mail')}</Label>
            </FormGroup>

            <FormGroup floating>
              <Input id='InputPassword' onChange={handleInputPassword} type={passwordType} placeholder=' ' required />
              <div className="input-group-btn" id='iconePassword'>
                <span className="btn btn-outline-primary" onClick={onClickEye}>
                  {passwordType === "password" ? <ImEye id="iconChange" className="change-button" size={25} /> : <ImEyeBlocked id="iconChange" className="change-button" size={25} />}
                </span>
              </div>
              <Label htmlFor='InputPassword'>{t('LOGIN.LABEL.mot_de_passe')}</Label>
            </FormGroup>

            <Alert color='danger' isOpen={showAlert} toggle={handleCloseAlert}><p>{message}</p></Alert>

            <div className='container_button'>
              <p className='lost_password form-group'>
                <a href={process.env.PUBLIC_URL + ROUTE.MOT_DE_PASSE_OUBLIE}>{t('LOGIN.mot_de_passe_oublie')}</a>
              </p>
              <p className='submit'>
                <Button className="action-button loadBtn" type="submit" disabled={loadingConnexion}>
                  <b>{t('LOGIN.LABEL.connexion')}</b>
                  <ClipLoader className="loadSpinner" color="#ffffff" size={15} loading={loadingConnexion} />
                </Button>
              </p>
            </div>
          </Form>
        </Col>
        <Col sm={12} lg={6} className='col-inscription'>
          {panier ? getFormulairePanier() : getComptInscription()}
        </Col>
      </Row>
    </div>
  )
}